import * as blogContStyle from "./content.module.scss";
import * as yup from "yup";
import React, { useEffect, useRef, useState } from "react";

import BlogListing from "../../Stories/BlogList/BlogListing";
import Facebook from "../../Icon/Facebook";
import LinkedIn from "../../Icon/LinkedIn";
import ReactPlayer from "react-player";
import { StaticImage } from "gatsby-plugin-image";
import Twitter from "../../Icon/Twitter";
import { navigate } from "gatsby";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { ndeFormInstance } from "../../../lib/axios-client";
import { usePostHog } from "posthog-js/react";
import BlogStickyBanner from "../BlogStickyBanner/BlogStickyBanner";
import { Loader } from "@mantine/core";
import { GoArrowRight } from "react-icons/go";
import BlogFaq from "../BlogFaq/BlogFaq";
import { usePageWrapperContext } from '../../PageWrapper/PageWrapper';

import banner1 from '../../../images/banner/banner-1.jpg'
import banner2 from '../../../images/banner/banner-2.jpg'
import banner3 from '../../../images/banner/banner-3.jpg'
import banner4 from '../../../images/banner/banner-4.jpg'
import banner5 from '../../../images/banner/banner-5.jpg'
import banner6 from '../../../images/banner/banner-6.jpg'
import banner7 from '../../../images/banner/banner-7.jpg'
import LinkedInFollowButton from "../../LinkedInFollowButton/LinkedInFollowButton";

export default function BlogContent({
  title,
  description,
  date,
  updatedDate,
  author,
  authorImage,
  readTime,
  audioUrl,
  path,
  recentBlogList,
  blogFaq,
}) {
  const { changeModalState } = usePageWrapperContext();
  const [apiLoading, setApiLoading] = useState(false);
  const posthog = usePostHog();

  const schema = yup.object({
    name: yup
      .string()
      .required("This field is required")
      .transform((value) => value.trim()),
    email: yup
      .string()
      .required("This field is required!")
      .transform((value) => value.trim())
      .email("Please enter a valid email!")
      .test(
        "no-consecutive-dots",
        "Email cannot contain consecutive dots. Please enter a valid email.",
        (value) => !value || !/\.\./.test(value)
      ),
    phone: yup
      .string()
      .nullable()
      .matches(/^(\+\d{1,4}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/, {
        message: "Please enter a valid phone number",
        excludeEmptyString: true,
      }),
    message: yup
      .string()
      .required("This field is required")
      .max(250, "Message must be 250 characters or less")
      .transform((value) => value.trim()),
  });
  const shareURl = `https://www.designstudiouiux.com${path}`;

  const [tableContentMenu, settableContentMenu] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    reValidateMode: "onChange",
  });

  const postEl = useRef<HTMLDivElement | null>(null);

  const onSubmit = async (data) => {
    setApiLoading(true);
    const landing_page = sessionStorage.getItem("landing_page");

    try {
      await ndeFormInstance.post("/lead-tracking", {
        ...data,
        form_id: 'blog-details',
        landing_page,
      });
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "contact_form_submitted",
          ...getValues(),
        });
      }
      posthog.capture("form_submitted", {
        form_id: "blog_content_form",
        submission_status: "success",
      });
      navigate("/thank-you");
    } catch (error) {
      toast.error(error.message);
      console.error(error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    const parentEl = document.createElement("div");
    parentEl.innerHTML = description;

    const tableContentHeadline = parentEl.querySelectorAll("h2, h3");

    const toc = Object.keys(tableContentHeadline).map((key) => {
      const str = tableContentHeadline[key].innerText
        .toLowerCase()
        .replace(".", "-") // replace a dot by a dash
        .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
        .replace(/\s+/g, "-") // collapse whitespace and replace by a dash
        .replace(/-+/g, "-") // collapse dashes
        .replace(/\//g, "")
        .replace(/(^\d*-)/g, "");

      tableContentHeadline[key].setAttribute("id", str);

      return tableContentHeadline[key];
    });

    const structuredtoc = toc.reduce((current, headerEl) => {
      if (headerEl.tagName.toLowerCase() === "h2") {
        headerEl.childh3 = [];
        current.push(headerEl);
      } else {
        if (current.length) {
          current[current.length - 1].childh3.push(headerEl);
        }
      }

      return current;
    }, []);

    postEl.current.innerHTML = parentEl.innerHTML;

    settableContentMenu(structuredtoc);
  }, [description]);


  // Banner inject logic dynamically 
  useEffect(() => {
    const banners = [
      { id: 'banner-1',  image: banner1 },
      { id: 'banner-2',  image: banner2 },
      { id: 'banner-3',  image: banner3 },
      { id: 'banner-4',  image: banner4 },
      { id: 'banner-5',  image: banner5 },
      { id: 'banner-6',  image: banner6 },
      { id: 'banner-7',  image: banner7 },
    ];

    const parentEl = postEl.current;
    if (!parentEl) return;
    
    // Get all content elements 
    const allElements = Array.from(parentEl.querySelectorAll("*"));
    const paragraphs = Array.from(parentEl.querySelectorAll("p"));
    const totalParagraphs = paragraphs.length;
    
    // Skip if content is too short
    if (totalParagraphs <= 3) return;
    
    // Determine target number of banners based on content length
    // Ensure we try to place at least 4 banners if there are enough paragraphs
    const targetBanners = totalParagraphs >= 12 ? 4 : Math.max(2, Math.floor(totalParagraphs / 4));
    
    // Find all h2, h3 positions to use for banner placement later
    const headerPositions = [];
    allElements.forEach((element, index) => {
      if (element.tagName === "H2" || element.tagName === "H3") {
        headerPositions.push(index);
      }
    });
    
    // ========== STEP 1: FIND ALL POTENTIAL BANNER POSITIONS (DRY RUN) ==========
    
    // Store all potential banner positions with their priority
    // Higher priority number = better position
    let potentialPositions = [];
    
    // Helper function that checks if a paragraph meets the banner injection criteria
    const meetsPreferredCriteria = (element) => {
      return (
        element.tagName === "P" && 
        element.previousElementSibling?.tagName === "P" &&
        element.nextElementSibling?.tagName === "P" &&
        !(element.previousElementSibling?.classList.contains("banner-wrapper")) &&
        !(element.nextElementSibling?.classList.contains("banner-wrapper"))
      );
    };
    
    // Relaxed criteria for when preferred positions aren't available
    const meetsRelaxedCriteria = (element, paragraphIndex, existingPositions = []) => {
      // Don't place banner if current element is not a paragraph
      if (element.tagName !== "P") return false;
      
      // Don't place banner if previous element is a banner
      if (element.previousElementSibling?.classList.contains("banner-wrapper")) return false;
      
      // Don't place banner if next element is a banner
      if (element.nextElementSibling?.classList.contains("banner-wrapper")) return false;
      
      // Don't place banner if this paragraph is already in candidates list
      if (existingPositions.includes(paragraphIndex)) return false;
      
      // Don't place banner if next element is a header (h2/h3)
      if (element.nextElementSibling?.tagName === "H2" || element.nextElementSibling?.tagName === "H3") return false;
      
      // If we have existing positions, ensure minimum spacing between them
      if (existingPositions.length > 0) {
        const minSpacing = Math.max(2, Math.floor(totalParagraphs / (targetBanners * 3)));
        const tooCloseToExistingPosition = existingPositions.some(
          idx => Math.abs(paragraphIndex - idx) < minSpacing
        );
        if (tooCloseToExistingPosition) return false;
      }
      
      return true;
    };
    
    // Get early paragraphs for first banner placement (priority 5)
    const earlyParagraphRange = Math.min(6, Math.floor(totalParagraphs * 0.2) + 2);
    for (let i = 2; i < earlyParagraphRange; i++) {
      const element = paragraphs[i];
      if (meetsPreferredCriteria(element)) {
        potentialPositions.push({ 
          index: i, 
          element: paragraphs[i], 
          priority: 5,
          type: 'early'
        });
      } else if (meetsRelaxedCriteria(element, i)) {
        potentialPositions.push({ 
          index: i, 
          element: paragraphs[i], 
          priority: 4,
          type: 'early-relaxed'
        });
      }
    }
    
    // Find paragraphs after headers (priority 4)
    headerPositions.forEach((headerPos, idx) => {      
      // Find paragraphs after this header
      let paragraphsAfterHeader = [];
      let foundParagraphs = 0;
      
      for (let j = headerPos + 1; j < allElements.length && foundParagraphs < 3; j++) {
        if (allElements[j].tagName === "P") {
          const paraIndex = paragraphs.indexOf(allElements[j]);
          paragraphsAfterHeader.push({
            element: allElements[j], 
            index: paraIndex
          });
          foundParagraphs++;
        }
      }
      
      // Prioritize the 2nd paragraph after header
      if (paragraphsAfterHeader.length >= 2) {
        const para = paragraphsAfterHeader[1];
        if (meetsRelaxedCriteria(para.element, para.index, potentialPositions.map(p => p.index))) {
          potentialPositions.push({
            index: para.index,
            element: para.element,
            priority: 4,
            type: 'after-header-2nd'
          });
        }
      }
      
      // Also consider 1st and 3rd paragraphs
      if (paragraphsAfterHeader.length >= 1) {
        // First paragraph
        const para1 = paragraphsAfterHeader[0];
        if (meetsRelaxedCriteria(para1.element, para1.index, potentialPositions.map(p => p.index))) {
          potentialPositions.push({
            index: para1.index,
            element: para1.element,
            priority: 3,
            type: 'after-header-1st'
          });
        }
        
        // Third paragraph if available
        if (paragraphsAfterHeader.length >= 3) {
          const para3 = paragraphsAfterHeader[2];
          if (meetsRelaxedCriteria(para3.element, para3.index, potentialPositions.map(p => p.index))) {
            potentialPositions.push({
              index: para3.index,
              element: para3.element,
              priority: 3,
              type: 'after-header-3rd'
            });
          }
        }
      }
    });
    
    // Find regularly spaced positions for even distribution (priority 3)
    const evenSections = Math.min(8, Math.ceil(totalParagraphs / 5)); // max 8 candidate sections
    
    for (let i = 1; i <= evenSections; i++) {
      const targetPos = Math.floor(i * (totalParagraphs / (evenSections + 1)));
      
      // Look for suitable paragraphs near the target position
      const searchRange = Math.floor(totalParagraphs / (evenSections * 2));
      
      for (let offset = 0; offset < searchRange; offset++) {
        // Try positions both before and after the target
        for (const posOffset of [offset, -offset]) {
          const pos = targetPos + posOffset;
          
          // Skip if out of bounds
          if (pos < 0 || pos >= paragraphs.length) continue;
          
          const element = paragraphs[pos];
          
          if (meetsRelaxedCriteria(element, pos, potentialPositions.map(p => p.index))) {
            potentialPositions.push({
              index: pos,
              element: element,
              priority: 3 - (offset * 0.1), // Slight decrease in priority as we move away from target
              type: 'even-distribution'
            });
            break; // Found a position near this target, move to next section
          }
        }
      }
    }
    
    // Add fallback positions with priority 1 (every 3rd paragraph that meets basic criteria)
    for (let i = 3; i < paragraphs.length - 1; i += 3) {
      const element = paragraphs[i];
      const paraIndex = i;
      
      if (element.tagName === "P" && 
          !(element.previousElementSibling?.classList.contains("banner-wrapper")) &&
          !(element.nextElementSibling?.tagName === "H2" || element.nextElementSibling?.tagName === "H3") &&
          !potentialPositions.some(p => p.index === paraIndex)) {
        potentialPositions.push({
          index: paraIndex,
          element: element,
          priority: 1,
          type: 'fallback'
        });
      }
    }
    
    // Log all potential positions for debugging
    console.log('Potential banner positions:', potentialPositions.length);
    
    // ========== STEP 2: SELECT OPTIMAL BANNER POSITIONS ==========
    
    // Sort positions by priority (highest first)
    potentialPositions.sort((a, b) => b.priority - a.priority);
    
    // Filter positions by type
    const earlyPositions = potentialPositions.filter(p => p.type.startsWith('early'));
    const afterHeaderPositions = potentialPositions.filter(p => p.type.startsWith('after-header'));
    const distributedPositions = potentialPositions.filter(p => p.type === 'even-distribution');
    const fallbackPositions = potentialPositions.filter(p => p.type === 'fallback');
    
    // Final selected positions in ascending order by index
    let selectedPositions = [];
    let selectedIndices = [];
    
    // Always try to place first banner early
    if (earlyPositions.length > 0) {
      selectedPositions.push(earlyPositions[0]);
      selectedIndices.push(earlyPositions[0].index);
    }
    
    // Calculate how many banners to place after headers vs distributed
    const remainingBanners = targetBanners - selectedPositions.length;
    let afterHeaderCount = Math.min(afterHeaderPositions.length, Math.ceil(remainingBanners / 2));
    let distributedCount = remainingBanners - afterHeaderCount;
    
    // Select after-header positions, ensuring no overlap with already selected positions
    for (let i = 0; i < afterHeaderPositions.length && selectedPositions.length < targetBanners && afterHeaderCount > 0; i++) {
      const pos = afterHeaderPositions[i];
      
      // Check for minimum spacing between already selected positions
      const minSpacing = Math.max(2, Math.floor(totalParagraphs / (targetBanners * 2)));
      const tooClose = selectedIndices.some(idx => Math.abs(idx - pos.index) < minSpacing);
      
      if (!tooClose) {
        selectedPositions.push(pos);
        selectedIndices.push(pos.index);
        afterHeaderCount--;
      }
    }
    
    // Select distributed positions to ensure even spacing throughout content
    for (let i = 0; i < distributedPositions.length && selectedPositions.length < targetBanners && distributedCount > 0; i++) {
      const pos = distributedPositions[i];
      
      // Check for minimum spacing between already selected positions
      const minSpacing = Math.max(2, Math.floor(totalParagraphs / (targetBanners * 2)));
      const tooClose = selectedIndices.some(idx => Math.abs(idx - pos.index) < minSpacing);
      
      if (!tooClose) {
        selectedPositions.push(pos);
        selectedIndices.push(pos.index);
        distributedCount--;
      }
    }
    
    // If we still don't have enough positions, use fallbacks
    if (selectedPositions.length < targetBanners) {
      for (let i = 0; i < fallbackPositions.length && selectedPositions.length < targetBanners; i++) {
        const pos = fallbackPositions[i];
        
        // Check for minimum spacing between already selected positions
        const minSpacing = Math.max(1, Math.floor(totalParagraphs / (targetBanners * 3)));
        const tooClose = selectedIndices.some(idx => Math.abs(idx - pos.index) < minSpacing);
        
        if (!tooClose) {
          selectedPositions.push(pos);
          selectedIndices.push(pos.index);
        }
      }
    }
    
    // Sort selected positions by paragraph index (ascending)
    selectedPositions.sort((a, b) => a.index - b.index);
    
    // If we really don't have enough positions, add random ones that meet minimum criteria
    if (selectedPositions.length < Math.min(targetBanners, 4) && paragraphs.length > 5) {
      // Find additional positions with very relaxed criteria
      for (let i = 2; i < paragraphs.length - 1 && selectedPositions.length < Math.min(targetBanners, 4); i++) {
        if (!selectedIndices.includes(i) && 
            paragraphs[i].tagName === "P" &&
            !(paragraphs[i].nextElementSibling?.tagName === "H2" || paragraphs[i].nextElementSibling?.tagName === "H3") &&
            !selectedIndices.some(idx => Math.abs(idx - i) < 2)) {
          
          selectedPositions.push({
            index: i,
            element: paragraphs[i],
            priority: 0,
            type: 'emergency-fallback'
          });
          selectedIndices.push(i);
        }
      }
      
      // Re-sort after adding emergency positions
      selectedPositions.sort((a, b) => a.index - b.index);
    }
    
    // ========== STEP 3: INSERT THE BANNERS AT SELECTED POSITIONS ==========
    // Helper function to create and insert a banner
    let bannerIndex = 1;
    const createAndInsertBanner = (element) => {
      bannerIndex = bannerIndex + 1;
      // Select a random banner, avoiding duplicates when possible
      let availableBanners = banners;
      
      // Get already used banner IDs from existing banners on page
      const usedBannerIds = Array.from(document.querySelectorAll('.banner-wrapper img'))
        .map(img => img.alt.replace('Banner ', ''));
      
      // Filter out already used banners if possible
      if (usedBannerIds.length > 0 && usedBannerIds.length < banners.length) {
        const unusedBanners = banners.filter(b => !usedBannerIds.includes(b.id));
        if (unusedBanners.length > 0) {
          availableBanners = unusedBanners;
        }
      }
      
      // Select random banner from available ones
      const randomBanner = availableBanners[Math.floor(Math.random() * availableBanners.length)];
      
      // Create banner elements
      const img = document.createElement("img");
      img.src = randomBanner.image;
      img.alt = `Banner ${randomBanner.id}`;
      img.loading = "lazy";
      img.width = 830;
      img.height = 200;
      img.style.maxWidth = "100%";

      const link = document.createElement("a");
      link.href = randomBanner.link;
      link.style.display = "block";

      // Add PostHog analytics for banner clicks
      link.addEventListener("click", (e) => {
        e.preventDefault();
       
        changeModalState(true);
        const index = insertedBanners.findIndex(e => e.bannerId === randomBanner.id);
         posthog.capture("blog_banner_click", {
          banner_id: randomBanner.id,
          position: index
        });
        sessionStorage.setItem('blog_banner', JSON.stringify({ id: randomBanner.id, bannerIndex: index + 1 }))
      });

      link.appendChild(img);

      const wrapper = document.createElement("div");
      wrapper.classList.add("banner-wrapper");
      wrapper.style.display = "block";
      wrapper.style.margin = "20px 0";
      wrapper.appendChild(link);

      // Insert banner after the element
      element.insertAdjacentElement("afterend", wrapper);
      
      return randomBanner.id;
    };
    
    // Insert banners at the selected positions
    console.log(`Injecting ${selectedPositions.length} banners at positions:`, 
      selectedPositions.map(p => ({ index: p.index, type: p.type, priority: p.priority }))
    );
    
    let insertedBanners = [];
    selectedPositions.forEach(position => {
      const bannerId = createAndInsertBanner(position.element);
      insertedBanners.push({
        position: position.index,
        type: position.type,
        priority: position.priority,
        bannerId
      });
    });
    
    console.log('Banner injection complete:', insertedBanners);
  }, [posthog, description]);

  const [active, setActive] = useState<string | null>(null);
  const navigateAction = (e, id) => {
    e?.preventDefault();

    const element = document.querySelector(`#${id}`);
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - headerOffset;
    window.scrollTo(0, offsetPosition, { behavior: "smooth" });

    setActive(id);
  };
  // End Banner inject logic 


  // -----===== start intersection observer =====-----
  useEffect(() => {
    if (!postEl.current) return;

    const headings = postEl.current.querySelectorAll('h2[id], h3[id]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('id');
            if (id) setActive(id);
          }
        });
      },
      { root: null, threshold: 0.1 }
    );

    headings.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // -----===== end intersection observer =====-----


  return (
    <section className={blogContStyle.blog__content}>
      <div className={blogContStyle.blog__content__container}>
        <div className={blogContStyle.table_of_data_section}>
          <aside className={blogContStyle.aside}>
            <h5>Table of Content</h5>
            <ul>
              {tableContentMenu.map((item, index) => (
                <li key={item.id}>
                  <span className={blogContStyle.tocRevealLinker}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => navigateAction(e, item.id)}
                      className={active === item.id ? "active" : ""}
                    >
                      {item.innerText}
                    </a>
                  </span>

                  {item.childh3 && item.childh3.length > 0 && (
                    <ul>
                      {item.childh3.map((inner) => (
                        <li key={inner.id}>
                          <a
                            href={`#${inner.id}`}
                            onClick={(e) => navigateAction(e, inner.id)}
                            className={active === inner.id ? "active" : ""}
                          >
                            {inner.innerText}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            <BlogStickyBanner />

            <div className={blogContStyle.blog__social__share}>
              <ul className={blogContStyle.blog__social__icons}>
                <li
                  className={`${blogContStyle.blog__social__item} ${blogContStyle.facebook}`}
                >
                  <a
                    className={blogContStyle.blog__social__link}
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    href={`https://www.facebook.com/sharer/sharer.php?u=${shareURl}`}
                    target="_blank"
                  >
                    <Facebook />
                  </a>
                </li>
                <li
                  className={`${blogContStyle.blog__social__item} ${blogContStyle.twitter}`}
                >
                  <a
                    className={blogContStyle.blog__social__link}
                    rel="noopener noreferrer"
                    aria-label="Twitter"
                    href={`https://twitter.com/intent/tweet?text=${shareURl}`}
                    target="_blank"
                  >
                    <Twitter />
                  </a>
                </li>
                <li
                  className={`${blogContStyle.blog__social__item} ${blogContStyle.linkedIn}`}
                >
                  <a
                    className={blogContStyle.blog__social__link}
                    rel="noopener noreferrer"
                    aria-label="Linkedin"
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareURl}`}
                    target="_blank"
                  >
                    <LinkedIn />
                  </a>
                </li>
              </ul>
              <p>Share this article</p>
            </div>
          </aside>
          <article className={blogContStyle.article}>
            <div className={blogContStyle.blog_main_article} id="blog-content">
              <h1>{title}</h1>
              <div className={blogContStyle.blog__content__meta}>
                <div className={blogContStyle.blog__content__author}>
                  <span className={blogContStyle.blog__content__author__img}>
                    <img
                      src={authorImage.replace("http://", "https://")}
                      alt={author}
                    />
                  </span>
                  <span className={blogContStyle.blog__content__author__name}>
                    {author}
                  </span>
                </div>
                <ul className={blogContStyle.blog__content__meta__details}>
                  {readTime && (
                    <li>
                      Read Time: <span>{readTime}</span>
                    </li>
                  )}

                  <li>
                    {date !== updatedDate ? "Updated" : "Published"}:{" "}
                    <span>{date !== updatedDate ? updatedDate : date}</span>
                  </li>
                </ul>
              </div>

              {audioUrl && (
                <div className={blogContStyle.blog__content__audio}>
                  <ReactPlayer url={audioUrl} width="100%" height="auto" />
                </div>
              )}

              <div
                ref={postEl}
                className={blogContStyle.blog__content__description}
              />

             
            </div>
             {!!blogFaq?.blogFaqData?.faq?.length && (
                <BlogFaq data={blogFaq} />
              )}
          </article>
        </div>
      </div>

      <LinkedInFollowButton />

      <div className={blogContStyle.recent_blog_section}>
        <div className={blogContStyle.recent_blog_container} id="recent-blog">
          <h2>Recent Articles</h2>
          <div className={blogContStyle.blogRow}>
            {recentBlogList.map((item, i) => (
              <BlogListing key={i} data={item} />
            ))}
          </div>
        </div>
      </div>
      <div className={blogContStyle.search_form}>
        <div className={blogContStyle.blog__content__container}>
          <div className={blogContStyle.blog_contact_section}>
            <div className={blogContStyle.research_content}>
              <h2>
                Looking for <span>UX/UI research</span>
              </h2>
              <h3>or product branding?</h3>
            </div>
            <form
              method="post"
              className={blogContStyle.formArea}
              id="blogDetailsForm"
              name="blogDetailsForm"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className={blogContStyle.emailArea}>
                <div
                  className={
                    errors.name
                      ? `${blogContStyle.formGroup} ${blogContStyle.error}`
                      : `${blogContStyle.formGroup}`
                  }
                >
                  <StaticImage
                    className={blogContStyle.envelope}
                    src="../../../images/user-icon.svg"
                    alt="user"
                    placeholder="blurred"
                  />
                  <input
                    type="text"
                    placeholder="Enter your name*"
                    autoComplete="name"
                    {...register("name")}
                  />
                </div>
                {errors.name && (
                  <span className={blogContStyle.errortxt}>
                    {errors.name.message}
                  </span>
                )}
              </div>

              <div className={blogContStyle.emailArea}>
                <div
                  className={
                    errors.email
                      ? `${blogContStyle.formGroup} ${blogContStyle.error}`
                      : `${blogContStyle.formGroup}`
                  }
                >
                  <StaticImage
                    className={blogContStyle.envelope}
                    src="../../../images/email-icon.svg"
                    alt="Envelope"
                    placeholder="blurred"
                  />
                  <input
                    type="email"
                    placeholder="Enter your email Id*"
                    autoComplete="email"
                    {...register("email")}
                  />
                  {errors.email || !getValues("email") ? (
                    ""
                  ) : (
                    <StaticImage
                      src="../../../images/fi-check-circle.png"
                      alt=""
                      width="15"
                      className={blogContStyle.verifiedemail}
                      placeholder="blurred"
                    />
                  )}
                </div>
                {errors.email && (
                  <span className={blogContStyle.errortxt}>
                    {errors.email.message}
                  </span>
                )}
              </div>
              <div className={blogContStyle.emailArea}>
                <div className={`${blogContStyle.formGroup}`}>
                  <StaticImage
                    className={blogContStyle.envelope}
                    src="../../../images/phone-icon.svg"
                    alt="Phone"
                    placeholder="blurred"
                  />
                  <input
                    type="tel"
                    placeholder="Phone number (optional)"
                    autoComplete="tel"
                    {...register("phone")}
                  />
                </div>
                {errors.phone && (
                  <span className={blogContStyle.errortxt}>
                    {errors.phone.message}
                  </span>
                )}
              </div>

              <div className={blogContStyle.emailArea}>
                <div
                  className={
                    errors.message
                      ? `${blogContStyle.formGroup} ${blogContStyle.error}`
                      : `${blogContStyle.formGroup}`
                  }
                >
                  <textarea
                    placeholder="Message*"
                    {...register("message")}
                  ></textarea>
                </div>
                {errors.message && (
                  <span className={blogContStyle.errortxt}>
                    {errors.message.message}
                  </span>
                )}
              </div>

              <div className={blogContStyle.submitButton}>
                <button type="submit" disabled={apiLoading} style={{ cursor: `${apiLoading ? 'not-allowed' : 'pointer'}`}}>
                  <span>There We Go!</span>
                  {apiLoading && <Loader size={24} color="#fff" />}
                  {!apiLoading && <GoArrowRight />}
                </button>
                <p className={blogContStyle.tagText}>250+ Projects Delivered — And Counting</p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
